import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, ArrowSquareOut, Lightning } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const t = typeof iso === 'string' ? Date.parse(iso) : iso;
  if (!t || isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const HoldingsNewsFeed = ({ hasHoldings }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasHoldings) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/portfolio/news?limit=8');
        if (!cancelled) setArticles(res.data.articles || []);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [hasHoldings]);

  if (!hasHoldings) return null;
  if (!loading && articles.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="mb-12"
      data-testid="holdings-news-section"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Newspaper size={22} weight="fill" className="text-gold" />
          <h2 className="text-2xl font-unbounded font-bold text-white">News for Your Holdings</h2>
          <Lightning size={14} weight="fill" className="text-yellow-400 animate-pulse" />
        </div>
        <button
          onClick={() => navigate('/news')}
          className="text-gold hover:text-gold-light font-bold transition-colors text-sm"
        >
          All News →
        </button>
      </div>

      {loading ? (
        <div className="glass-card p-6 text-center text-zinc-400 text-sm">Loading news for your holdings...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {articles.slice(0, 6).map((article, i) => (
            <motion.a
              key={`${article.symbol}-${i}`}
              href={article.url || article.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className="glass-card p-4 hover:scale-[1.01] transition-transform group flex gap-3"
              data-testid={`holdings-news-${i}`}
            >
              <div className="flex-shrink-0 w-12 flex flex-col items-center justify-start pt-1">
                <span className="px-2 py-1 rounded-md bg-gold/20 text-gold text-[10px] font-bold tracking-wide">
                  {article.symbol}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium leading-snug line-clamp-2 group-hover:text-gold transition-colors">
                  {article.title || article.headline}
                </p>
                <div className="flex items-center gap-2 mt-1.5 text-xs text-zinc-500">
                  <span>{article.publisher || article.source || 'Source'}</span>
                  <span>·</span>
                  <span>{formatTimeAgo(article.published_at || article.publishedAt)}</span>
                </div>
              </div>
              <ArrowSquareOut size={14} className="text-zinc-600 group-hover:text-gold flex-shrink-0 mt-1" />
            </motion.a>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default HoldingsNewsFeed;
